import React from "react";
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import { ErrorText, FieldLabel, inputSx, MUTED } from "./builderUi";

/**
 * Builder triggers: who the workflow is for, what it watches, and — for
 * reminders — when it fires relative to the due date. Stored on the
 * workflow as audience / triggerId / dueTiming / dueDays
 * (triggerModel stays "event").
 */

export const AUDIENCE_OPTIONS = [
  { value: "coach", label: "For coach" },
  { value: "member", label: "For member" },
];

export const TRIGGERS_BY_AUDIENCE = {
  coach: [
    { value: "coach_general_review", label: "General submission review" },
    { value: "coach_scheduled_review", label: "Scheduled submission review" },
  ],
  member: [
    { value: "submission_pending", label: "Submission reminder" },
    { value: "new_submission", label: "New Submission" },
  ],
};

/**
 * When options per trigger. Coach reviews count from the submission date,
 * member reminders from the check-in due date. A general review fires on
 * submission or N days after it — at least 1, since the daily run starts
 * before that day's submissions come in.
 */
const TIMING_BY_TRIGGER = {
  coach_general_review: [
    { value: "on", label: "On submission", daysLabel: "" },
    {
      value: "after",
      label: "After submission",
      daysLabel: "Days after submission",
      minDays: 1,
      minDaysError:
        "Enter 1 or more days. For the same day, choose On submission.",
    },
  ],
  coach_scheduled_review: [
    {
      value: "before",
      label: "Before submission date",
      daysLabel: "Days before submission date",
    },
    {
      value: "after",
      label: "After submission date",
      daysLabel: "Days after submission date",
    },
  ],
  submission_pending: [
    {
      value: "before",
      label: "Before due date",
      daysLabel: "Days before due date",
    },
    {
      value: "after",
      label: "After due date",
      daysLabel: "Days after due date",
    },
  ],
};

export const timingOptionsFor = (triggerId) =>
  TIMING_BY_TRIGGER[triggerId] || [];

const timingOption = (triggerId, dueTiming) =>
  timingOptionsFor(triggerId).find((option) => option.value === dueTiming) ||
  null;

/** This trigger + timing asks for a number of days. */
export const needsDays = (triggerId, dueTiming) =>
  Boolean(timingOption(triggerId, dueTiming)?.daysLabel);

const minDaysFor = (triggerId, dueTiming) =>
  timingOption(triggerId, dueTiming)?.minDays || 0;

const COACH_REVIEW_OFF =
  "Coach review is off for this tracker, so coach emails can't be sent.";
const GENERAL_REVIEW_OFF =
  "This tracker doesn't review every check-in (Coach replies on every check-in? is No), so this can't send.";

// Same rule as normalizeAlsoReviewEveryCheckin in bms-backend
// coach_review_helpers: does a submission open a check-in review?
const repliesToEveryCheckin = (setup, coachReview) => {
  if (String(setup.scheduledReviews || "none") === "none") return true;
  if (typeof setup.alsoReviewEveryCheckin === "boolean") {
    return setup.alsoReviewEveryCheckin;
  }
  if (setup.coachReviewMode === "each_checkin") return true;
  return String(coachReview.trigger || "") === "after_each_submission";
};

/**
 * Coach reviews the tracker can send, by the backend's rules: none when
 * coach review is off (lead trackers), general (every check-in) reviews only
 * when the coach replies to every check-in. Unknown tracker → everything.
 */
export const coachReviewSupport = (tracker) => {
  if (!tracker) return { coach: true, general: true };
  const coachReview = tracker.coachReview || {};
  if (coachReview.enabled === false) return { coach: false, general: false };
  return {
    coach: true,
    general: repliesToEveryCheckin(tracker.createSetup || {}, coachReview),
  };
};

const FULL_SUPPORT = { coach: true, general: true };

// Fires on the event itself — no due date to ask about.
const NO_TIMING_TRIGGERS = new Set(["new_submission"]);

export const needsTiming = (triggerId) =>
  Boolean(triggerId) && !NO_TIMING_TRIGGERS.has(triggerId);

const BUILDER_TRIGGER_IDS = new Set(
  Object.values(TRIGGERS_BY_AUDIENCE)
    .flat()
    .map((option) => option.value)
);

export const isBuilderTrigger = (workflow) =>
  (workflow?.triggerModel || "event") === "event" &&
  BUILDER_TRIGGER_IDS.has(workflow?.triggerId);

export const audienceForTrigger = (triggerId) => {
  if (TRIGGERS_BY_AUDIENCE.coach.some((t) => t.value === triggerId)) {
    return "coach";
  }
  if (TRIGGERS_BY_AUDIENCE.member.some((t) => t.value === triggerId)) {
    return "member";
  }
  return "";
};

export const recipientsForAudience = (audience) =>
  audience === "coach" ? "coach" : "person";

const BASE_INSERT_FIELDS = [
  { token: "first_name", label: "First Name" },
  { token: "full_name", label: "Full Name" },
  { token: "email", label: "Email" },
  { token: "coach_name", label: "Coach" },
  { token: "journey_name", label: "Journey Name" },
];

// Name / email / phone questions — already covered by the fields above.
const PERSONAL_INFO_QUESTION_IDS = new Set([
  "q_first_name",
  "q_last_name",
  "q_email",
  "q_phone",
]);

// Triggers that run on a submission, so its answers are filled in.
const ANSWER_TRIGGERS = new Set(["new_submission", "coach_general_review"]);

/**
 * "Insert field" chips for a trigger — only tokens the backend fills for
 * it. `trackerFields` = getDynamicFieldsForTracker(...) (for the answers).
 */
export const insertFieldsFor = (config = {}, trackerFields = []) => {
  const { triggerId, dueTiming } = config;
  const fields = [...BASE_INSERT_FIELDS];
  if (triggerId === "submission_pending") {
    fields.push({ token: "follow_up_date", label: "Due Date" });
    if ((config.schedulePointKeys || []).length) {
      fields.push({ token: "current_week_stage", label: "Week" });
    }
  }
  if (triggerId === "coach_general_review" && dueTiming === "after") {
    fields.push({ token: "review_date", label: "Submission Date" });
  }
  if (triggerId === "coach_scheduled_review") {
    fields.push({ token: "review_date", label: "Review Date" });
    fields.push({ token: "current_week_stage", label: "Week" });
  }
  if (!triggerId || ANSWER_TRIGGERS.has(triggerId)) {
    trackerFields
      .filter(
        (field) =>
          field.group === "Journey Responses" &&
          !PERSONAL_INFO_QUESTION_IDS.has(field.questionId)
      )
      .forEach((field) => fields.push(field));
  }
  return fields.map((field) => ({ group: "Standard", ...field }));
};

export const emptyTriggerConfig = () => ({
  audience: "",
  triggerId: "",
  dueTiming: "",
  dueDays: "",
  // [] = every schedule; otherwise the chosen journey schedule points.
  schedulePointKeys: [],
});

const isBlank = (value) => value === "" || value == null;

const POINT_UNITS = new Set(["days", "weeks", "months"]);

const toSchedulePoint = (point) => {
  const amount = Number(point?.amount);
  const unit = String(point?.unit || "");
  if (!point?.key || !Number.isFinite(amount) || amount <= 0) return null;
  if (!POINT_UNITS.has(unit)) return null;
  return {
    key: String(point.key),
    label: point.label || point.key,
    amount,
    unit,
  };
};

/**
 * The tracker's journey schedule points (Check-in Configuration →
 * Journey Submission scheduling). Journeys only. Points saved on the
 * workflow that the tracker no longer has stay listed.
 */
export const schedulePointsOf = (tracker, savedWorkflow = null) => {
  const points = tracker?.createSetup
    ? (Array.isArray(tracker.reviewPointSettings)
        ? tracker.reviewPointSettings
        : []
      )
        .map(toSchedulePoint)
        .filter(Boolean)
    : [];
  const savedCopies = [
    ...(Array.isArray(savedWorkflow?.schedulePoints)
      ? savedWorkflow.schedulePoints
      : []),
    savedWorkflow?.schedulePoint,
  ]
    .map(toSchedulePoint)
    .filter(Boolean);
  savedCopies.forEach((saved) => {
    if (!points.some((point) => point.key === saved.key)) points.push(saved);
  });
  return points;
};

/** Chosen point keys of a workflow (list, or the older single key). */
const workflowPointKeys = (workflow) => {
  if (Array.isArray(workflow?.schedulePointKeys)) {
    return workflow.schedulePointKeys.map(String).filter(Boolean);
  }
  return workflow?.schedulePointKey ? [String(workflow.schedulePointKey)] : [];
};

/** Trigger settings of a saved workflow ("" fields for older workflows). */
export const triggerConfigFromWorkflow = (workflow) => {
  if (!isBuilderTrigger(workflow)) return emptyTriggerConfig();
  const audience = workflow.audience || audienceForTrigger(workflow.triggerId);
  const schedulePointKeys = workflowPointKeys(workflow);
  if (!needsTiming(workflow.triggerId)) {
    return {
      ...emptyTriggerConfig(),
      audience,
      triggerId: workflow.triggerId,
      schedulePointKeys,
    };
  }
  let { dueTiming, dueDays } = workflow;
  // Submission Pending saved before dueTiming existed.
  if (
    workflow.triggerId === "submission_pending" &&
    dueTiming !== "before" &&
    dueTiming !== "after"
  ) {
    if (!isBlank(workflow.pendingDaysBefore)) {
      dueTiming = "before";
      dueDays = workflow.pendingDaysBefore;
    } else if (!isBlank(workflow.overdueDaysAfter)) {
      dueTiming = "after";
      dueDays = workflow.overdueDaysAfter;
    }
  }
  if (!timingOption(workflow.triggerId, dueTiming)) dueTiming = "";
  return {
    audience,
    triggerId: workflow.triggerId,
    dueTiming: dueTiming || "",
    dueDays: isBlank(dueDays) ? "" : dueDays,
    schedulePointKeys,
  };
};

/**
 * Picking an audience resets the trigger; picking a trigger drops a
 * When it doesn't offer.
 */
export const applyTriggerChange = (config, patch) => {
  const next = { ...config, ...patch };
  if (patch.audience !== undefined && patch.audience !== config.audience) {
    next.triggerId = "";
  }
  if (!timingOption(next.triggerId, next.dueTiming)) next.dueTiming = "";
  return next;
};

/** `support` = coachReviewSupport(tracker): blocks coach reviews that can't send. */
export const validateTriggerConfig = (config, support = FULL_SUPPORT) => {
  const errors = {};
  if (!config.audience) errors.audience = "Choose who this is for";
  else if (config.audience === "coach" && !support.coach) {
    errors.audience = COACH_REVIEW_OFF;
  } else if (!config.triggerId) errors.triggerId = "Choose a trigger";
  else if (config.triggerId === "coach_general_review" && !support.general) {
    errors.triggerId = GENERAL_REVIEW_OFF;
  } else if (needsTiming(config.triggerId)) {
    if (!config.dueTiming) {
      errors.dueTiming = "Choose when";
    } else if (needsDays(config.triggerId, config.dueTiming)) {
      if (isBlank(config.dueDays)) {
        errors.dueDays = "Enter the number of days";
      } else if (
        Number(config.dueDays) < minDaysFor(config.triggerId, config.dueTiming)
      ) {
        errors.dueDays = timingOption(
          config.triggerId,
          config.dueTiming
        ).minDaysError;
      }
    }
  }
  return errors;
};

/**
 * Workflow fields for a trigger config (plus the old pending-day fields).
 * `schedulePoints` gives the copies of the chosen points saved with it.
 */
export const triggerPatch = (config, schedulePoints = []) => {
  const keys = config.schedulePointKeys || [];
  const chosen = schedulePoints.filter((item) => keys.includes(item.key));
  const base = {
    triggerModel: "event",
    triggerId: config.triggerId,
    audience: config.audience,
    recipients: recipientsForAudience(config.audience),
    schedulePointKeys: chosen.map((item) => item.key),
    schedulePoints: chosen,
    // Older single-point fields — replaced by the list above.
    schedulePointKey: "",
    schedulePoint: null,
  };
  if (!needsTiming(config.triggerId)) {
    return { ...base, dueTiming: "", dueDays: "" };
  }
  if (!needsDays(config.triggerId, config.dueTiming)) {
    return { ...base, dueTiming: config.dueTiming, dueDays: "" };
  }
  // 0 = on the due date itself.
  const days = Math.max(0, Math.floor(Number(config.dueDays) || 0));
  const patch = { ...base, dueTiming: config.dueTiming, dueDays: days };
  if (config.triggerId === "submission_pending") {
    patch.pendingDaysBefore = config.dueTiming === "before" ? days : "";
    patch.overdueDaysAfter = config.dueTiming === "after" ? days : "";
  }
  return patch;
};

/** Options may be `disabled` with a `hint` saying why (shown in the menu only). */
const SelectField = ({ label, value, options, onChange, error }) => {
  const labelOf = (selected) =>
    options.find((option) => option.value === selected)?.label || selected;
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <TextField
        select
        fullWidth
        size="small"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(error)}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) =>
            selected ? (
              labelOf(selected)
            ) : (
              <Box component="span" sx={{ color: MUTED }}>
                Select…
              </Box>
            ),
          SelectDisplayProps: { "aria-label": label },
        }}
        sx={inputSx}
      >
        <MenuItem value="" disabled>
          <Box component="span" sx={{ color: MUTED }}>
            Select…
          </Box>
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={Boolean(option.disabled)}
          >
            {option.hint ? (
              <ListItemText primary={option.label} secondary={option.hint} />
            ) : (
              option.label
            )}
          </MenuItem>
        ))}
      </TextField>
      {error ? <ErrorText>{error}</ErrorText> : null}
    </Box>
  );
};

/** Tick one or more schedule points; none ticked = every schedule. */
const ScheduleSelect = ({ value = [], points, onChange }) => {
  const labelOf = (key) =>
    points.find((point) => point.key === key)?.label || key;
  return (
    <Box>
      <FieldLabel>Schedule</FieldLabel>
      <TextField
        select
        fullWidth
        size="small"
        value={value}
        onChange={(e) => {
          const picked = e.target.value;
          const list = typeof picked === "string" ? picked.split(",") : picked;
          // Keep the tracker's order.
          onChange(
            points.map((point) => point.key).filter((key) => list.includes(key))
          );
        }}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) =>
            selected.length ? (
              selected.map(labelOf).join(", ")
            ) : (
              <Box component="span" sx={{ color: MUTED }}>
                Every schedule
              </Box>
            ),
          SelectDisplayProps: { "aria-label": "Schedule" },
        }}
        sx={inputSx}
      >
        {points.map((point) => (
          <MenuItem key={point.key} value={point.key}>
            <Checkbox
              size="small"
              checked={value.includes(point.key)}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText primary={point.label} />
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

/**
 * One two-column grid in this order: For, Schedule, Review / Trigger, When,
 * Days. Hidden fields leave no gap, so without Schedule the row reads
 * For + Review and When moves next to Days. Schedule shows when the tracker
 * has journey schedule points; Days only when the chosen When needs them
 * ("On submission" doesn't); New Submission skips When/Days.
 */
export const TriggerFields = ({
  value,
  onChange,
  errors = {},
  idPrefix,
  schedulePoints = [],
  support = FULL_SUPPORT,
}) => {
  // Coach reviews this tracker can't send stay visible but disabled.
  const audienceOptions = AUDIENCE_OPTIONS.map((option) =>
    option.value === "coach" && !support.coach
      ? {
          ...option,
          disabled: true,
          hint: "Coach review is off for this tracker",
        }
      : option
  );
  const triggerOptions = (TRIGGERS_BY_AUDIENCE[value.audience] || []).map(
    (option) =>
      option.value === "coach_general_review" && !support.general
        ? {
            ...option,
            disabled: true,
            hint: "Coach doesn't reply to every check-in",
          }
        : option
  );
  // Saved workflows that can't send say so before anyone presses Save.
  const audienceNote =
    value.audience === "coach" && !support.coach ? COACH_REVIEW_OFF : "";
  const triggerNote =
    value.triggerId === "coach_general_review" &&
    support.coach &&
    !support.general
      ? GENERAL_REVIEW_OFF
      : "";
  const daysId = `${idPrefix}-due-days`;
  const showTiming = needsTiming(value.triggerId);
  const daysLabel = timingOption(value.triggerId, value.dueTiming)?.daysLabel;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: 2,
      }}
    >
      <SelectField
        label="For"
        value={value.audience}
        options={audienceOptions}
        onChange={(audience) => onChange({ audience })}
        error={errors.audience || audienceNote}
      />
      {schedulePoints.length ? (
        <ScheduleSelect
          value={value.schedulePointKeys || []}
          points={schedulePoints}
          onChange={(schedulePointKeys) => onChange({ schedulePointKeys })}
        />
      ) : null}
      {value.audience ? (
        <SelectField
          label={value.audience === "coach" ? "Review" : "Trigger"}
          value={value.triggerId}
          options={triggerOptions}
          onChange={(triggerId) => onChange({ triggerId })}
          error={errors.triggerId || triggerNote}
        />
      ) : null}
      {value.audience && showTiming ? (
        <SelectField
          label="When"
          value={value.dueTiming}
          options={timingOptionsFor(value.triggerId)}
          onChange={(dueTiming) => onChange({ dueTiming })}
          error={errors.dueTiming}
        />
      ) : null}
      {showTiming && daysLabel ? (
        <Box>
          <FieldLabel htmlFor={daysId}>{daysLabel}</FieldLabel>
          <TextField
            id={daysId}
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: minDaysFor(value.triggerId, value.dueTiming) }}
            value={value.dueDays ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              onChange({
                dueDays:
                  raw === "" ? "" : Math.max(0, Math.floor(Number(raw)) || 0),
              });
            }}
            error={Boolean(errors.dueDays)}
            sx={inputSx}
          />
          {errors.dueDays ? <ErrorText>{errors.dueDays}</ErrorText> : null}
        </Box>
      ) : null}
    </Box>
  );
};
